import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Layers, ArrowLeft, Edit, Trash2, Globe, Network, Plus,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';

const VLANDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('ipam.vlans');
  
  const [vlan, setVlan] = useState(null);
  const [subnets, setSubnets] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState({ l2_domain_id: '', vlan_number: '', name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchVlan = async () => {
    try {
      const response = await api.get(`/ipam/vlans/${id}`);
      setVlan(response.data);
      setFormData({
        l2_domain_id: response.data.l2_domain_id?.toString() || '',
        vlan_number: response.data.vlan_number?.toString() || '',
        name: response.data.name || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Error al cargar VLAN');
      navigate('/ipam/vlans');
    }
  };

  const fetchSubnets = async () => {
    try {
      const response = await api.get(`/ipam/subnets?vlan_id=${id}&limit=100`);
      setSubnets(response.data.data || []);
    } catch (error) {
      console.error('Error loading subnets:', error);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await api.get('/ipam/l2-domains/all');
      setDomains(response.data || []);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVlan(), fetchSubnets(), fetchDomains()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    if (!formData.l2_domain_id || !formData.vlan_number || !formData.name.trim()) {
      toast.error('L2 Domain, Número y Nombre son requeridos');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/ipam/vlans/${id}`, {
        ...formData,
        l2_domain_id: parseInt(formData.l2_domain_id),
        vlan_number: parseInt(formData.vlan_number)
      });
      toast.success('VLAN actualizada');
      setEditModalOpen(false);
      fetchVlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ipam/vlans/${id}`);
      toast.success('VLAN eliminada');
      navigate('/ipam/vlans');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!vlan) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="vlan-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ipam/vlans')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Layers className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  VLAN {vlan.vlan_number} - {vlan.name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  L2 Domain: {vlan.l2_domain_name}
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={() => setEditModalOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Número VLAN</span>
              <p className="font-mono font-bold text-primary text-xl">{vlan.vlan_number}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Nombre</span>
              <p className="font-medium">{vlan.name}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">L2 Domain</span>
              <Link to={`/ipam/l2-domains/${vlan.l2_domain_id}`} className="block">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-sm bg-muted hover:bg-muted/80">
                  <Network className="h-3 w-3 mr-1" />
                  {vlan.l2_domain_name}
                </span>
              </Link>
            </div>
            <div className="md:col-span-2">
              <span className="text-sm text-muted-foreground">Descripción</span>
              <p className="font-medium">{vlan.description || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Subnets Asociadas</span>
              <p className="font-medium text-primary">{subnets.length}</p>
            </div>
          </CardContent>
        </Card>

        {/* Subnets List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Subnets en esta VLAN ({subnets.length})
            </CardTitle>
            <Link to="/ipam/subnets">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Subnet
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {subnets.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No hay Subnets en esta VLAN</p>
            ) : (
              <div className="space-y-2">
                {subnets.map((subnet) => (
                  <Link 
                    key={subnet.id} 
                    to={`/ipam/subnets/${subnet.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-primary">
                        {subnet.network}/{subnet.mask}
                      </span>
                      {subnet.description && (
                        <span className="text-sm text-muted-foreground">- {subnet.description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-muted px-2 py-1 rounded">
                        {subnet.occupied_ips || 0} / {subnet.total_hosts || 0} IPs comprometidas
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                Editar VLAN
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>L2 Domain *</Label>
                <Select value={formData.l2_domain_id} onValueChange={(v) => setFormData(prev => ({ ...prev, l2_domain_id: v }))}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Seleccionar dominio" />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número VLAN *</Label>
                <Input
                  type="number"
                  min="1"
                  max="4094"
                  value={formData.vlan_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, vlan_number: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar VLAN <strong>{vlan.vlan_number} - {vlan.name}</strong>?
              {subnets.length > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Esta VLAN tiene {subnets.length} subnets asociadas.
                </span>
              )}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default VLANDetail;
